import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { schoolsApi } from '../utils/apiService';
import { formatSchoolName } from '../utils/nameFormatter';
import { categorizeSchools } from '../utils/schoolCategorization';
import { detectSchoolDataType } from '../utils/schoolDataDetection';
import { getDefaultTravelMode } from '../constants/travelModes';

const processEnrollmentData = (data) => {
  return data.map(school => {
    return {
      uuid: school.uuid,
      ncessch: school.ncessch, // Keep for backward compatibility
      ...school
    };
  });
};

// Every school selection and travel-mode switch takes the next number here. Responses check
// it before writing, so a slow request for a school the user has already navigated away from
// cannot land its data or clear the loading flags belonging to the current selection.
let schoolRequestSequence = 0;

const useSchoolStore = create(
  devtools((set, get) => ({
    // Separate initialization flags
    schoolInitialized: false,

    // Cache states - removed districtDataCache
    schoolDataCache: {},  // Cache by `${uuid}-${travelMode}`
    lastSchoolModified: null,

    // Data states
    schools: [],
    selectedSchool: null,
    esriData: null,
    nearbySchools: {},
    rawNearbySchools: {},
    enrollmentProjections: null,
    schoolsEnrollmentData: [],
    districtBoundaries: null,
    availableTravelModes: ['drive'],
    selectedTravelMode: 'drive',
    
    // Loading states
    schoolsLoading: false,
    contentLoading: false,
    loadingStates: {
      marketShare: false,
      nearby: false,
      projections: false,
      enrollment: false,
      districtBoundaries: false
    },
    error: null,

    // Action to fetch all schools
    fetchSchools: async () => {
      try {
        set({ schoolsLoading: true });
        const schools = await schoolsApi.getAllSchools();
        
        
        // Deduplicate schools by ncessch
        const uniqueSchools = Array.from(
          new Map(schools.map(school => [
            school.ncessch, 
            {...school, name: formatSchoolName(school.name)}
          ])).values()
        ).sort((a, b) => a.name.localeCompare(b.name));
    
        set({ schools: uniqueSchools, schoolsLoading: false });
      } catch (error) {
        console.error('Error fetching schools:', error);
        set({ error: error.message, schoolsLoading: false });
      }
    },
    
    // Action to set selected school and fetch its data
    selectSchool: async (school) => {
      const { schoolDataCache } = get();

      const requestId = ++schoolRequestSequence;
      const isStale = () => schoolRequestSequence !== requestId;

      // Check if school data needs refreshing (missing state_school_id)
      const needsRefresh = !school.state_school_id || school.state_school_id === 'undefined';
      
      let refreshedSchool = school;
      if (needsRefresh) {
        try {
          // Fetch fresh school data from API
          const allSchools = await schoolsApi.getAllSchools();
          const foundSchool = allSchools.find(s => s.uuid === school.uuid);
          if (foundSchool) {
            refreshedSchool = foundSchool;
          }
        } catch (error) {
          console.warn('Failed to refresh school data:', error);
          // Continue with original school data
        }
      }

      if (isStale()) return;

      // Immediately set selected school and start loading
      set({ 
        selectedSchool: refreshedSchool,
        contentLoading: true,
        // Initialize loading states for each data type
        loadingStates: {
          marketShare: true,
          nearby: true,
          projections: true,
          enrollment: true,
          districtBoundaries: true
        }
      });

      if (!refreshedSchool) {
        set({ contentLoading: false });
        return;
      }

      try {
        // Prefer a cached clipped mode when this school has one, otherwise drive.
        const driveCacheKey = `${refreshedSchool.uuid}-drive`;
        const driveCache = schoolDataCache[driveCacheKey];
        const cachedModes = driveCache?.marketShareData?.available_travel_modes
          || Object.keys(schoolDataCache)
            .filter((key) => key.startsWith(`${refreshedSchool.uuid}-`))
            .map((key) => key.slice(refreshedSchool.uuid.length + 1));
        const defaultMode = getDefaultTravelMode(cachedModes);
        const cacheKey = `${refreshedSchool.uuid}-${defaultMode}`;
        if (schoolDataCache[cacheKey]) {
          const cachedData = schoolDataCache[cacheKey];
          
          // Progressive updates from cache.
          // Travel mode has to be re-derived here as well as on the fetch path below.
          // Both live on the store rather than per school, so a cache hit that skips the
          // reset leaves the previous school's modes in the picker — e.g. selecting a
          // walk-enabled school and then returning to a drive-only one would still offer walk.
          set(state => ({ 
            esriData: cachedData.marketShareData,
            selectedTravelMode: defaultMode,
            availableTravelModes: cachedData.marketShareData?.available_travel_modes || cachedModes || ['drive'],
            loadingStates: { ...state.loadingStates, marketShare: false }
          }));
          set(state => ({ 
            nearbySchools: cachedData.nearbySchools,
            rawNearbySchools: cachedData.rawNearbySchools,
            loadingStates: { ...state.loadingStates, nearby: false }
          }));
          set(state => ({ 
            enrollmentProjections: cachedData.projectionsData,
            loadingStates: { ...state.loadingStates, projections: false }
          }));
          set(state => ({ 
            schoolsEnrollmentData: cachedData.enrollmentData,
            loadingStates: { ...state.loadingStates, enrollment: false }
          }));
          set(state => ({ 
            districtBoundaries: cachedData.districtBoundaries,
            loadingStates: { ...state.loadingStates, districtBoundaries: false }
          }));
          
          set({ contentLoading: false, error: null });
          return;
        }

        // If no cache, fetch new data with progressive updates
        try {
          // Detect if this is a location-only school
          const { isDropAPinLocation } = detectSchoolDataType(refreshedSchool, []);

          // Reset travel mode to 'drive' when switching schools
          set({ selectedTravelMode: 'drive', availableTravelModes: ['drive'] });

          // ESRI, projections, nearby schools and district boundaries have no dependencies on
          // each other, so they all go out at once. Only enrollment has to wait, since it needs
          // the nearby school UUIDs to know what to ask for.
          const [marketShareData, projectionsData, nearbySchoolsResponse, districtBoundariesData] =
            await Promise.all([
            schoolsApi.getMarketShareData(refreshedSchool.uuid, null, 'drive')
              .then(data => {
                if (isStale()) return data;
                const modes = data?.available_travel_modes || ['drive'];
                const preferred = getDefaultTravelMode(modes);
                set(state => ({
                  availableTravelModes: modes,
                  selectedTravelMode: preferred,
                  // Don't paint unconstrained rings if we're about to swap in a clipped mode.
                  ...(preferred === 'drive' ? {
                    esriData: data,
                    loadingStates: { ...state.loadingStates, marketShare: false }
                  } : {})
                }));
                return data;
              }),

            refreshedSchool.uuid && !isDropAPinLocation
              ? schoolsApi.getEnrollmentProjectionsV2(refreshedSchool.uuid)
                  .then(data => {
                    if (isStale()) return data;
                    set(state => ({
                      enrollmentProjections: data,
                      loadingStates: { ...state.loadingStates, projections: false }
                    }));
                    return data;
                  })
                  .catch(error => {
                    console.warn(`Projections not available for school ${refreshedSchool.uuid}:`, error);
                    if (isStale()) return null;
                    set(state => ({
                      enrollmentProjections: null,
                      loadingStates: { ...state.loadingStates, projections: false }
                    }));
                    return null;
                  })
              : Promise.resolve(null).then(() => {
                  if (isStale()) return null;
                  set(state => ({
                    enrollmentProjections: null,
                    loadingStates: { ...state.loadingStates, projections: false }
                  }));
                  return null;
                }),

            schoolsApi.getAllNearbySchools(refreshedSchool.uuid, null, 'drive')
              .then(data => {
                if (isStale()) return data;
                set(state => ({
                  nearbySchools: data,
                  rawNearbySchools: data,
                  loadingStates: { ...state.loadingStates, nearby: false }
                }));
                return data;
              })
              .catch(error => {
                console.warn(`Nearby schools not available for school ${refreshedSchool.uuid}:`, error);
                const emptyData = { current: {}, comparison: {}, gradeData: {}, baseline: null };
                if (isStale()) return emptyData;
                set(state => ({
                  nearbySchools: emptyData,
                  rawNearbySchools: emptyData,
                  loadingStates: { ...state.loadingStates, nearby: false }
                }));
                return emptyData;
              }),

            schoolsApi.getDistrictBoundaries(refreshedSchool.uuid)
              .then(data => {
                if (isStale()) return data;
                set(state => ({
                  districtBoundaries: data,
                  loadingStates: { ...state.loadingStates, districtBoundaries: false }
                }));
                return data;
              })
              .catch(error => {
                console.warn(`District boundaries not available for school ${refreshedSchool.uuid}:`, error);
                if (isStale()) return null;
                set(state => ({
                  districtBoundaries: null,
                  loadingStates: { ...state.loadingStates, districtBoundaries: false }
                }));
                return null;
              })
          ]);

          if (isStale()) return;

          const availableModes = marketShareData?.available_travel_modes || ['drive'];
          const defaultMode = getDefaultTravelMode(availableModes);
          let displayedEsri = marketShareData;
          let displayedNearby = nearbySchoolsResponse;
          let displayedMode = 'drive';

          if (defaultMode !== 'drive') {
            try {
              const [clippedEsri, clippedNearby] = await Promise.all([
                schoolsApi.getMarketShareData(refreshedSchool.uuid, null, defaultMode),
                schoolsApi.getAllNearbySchools(refreshedSchool.uuid, null, defaultMode)
              ]);
              if (isStale()) return;
              displayedEsri = clippedEsri;
              displayedNearby = clippedNearby;
              displayedMode = defaultMode;
              set(state => ({
                esriData: clippedEsri,
                nearbySchools: clippedNearby,
                rawNearbySchools: clippedNearby,
                selectedTravelMode: defaultMode,
                availableTravelModes: availableModes,
                loadingStates: {
                  ...state.loadingStates,
                  marketShare: false,
                  nearby: false
                }
              }));
            } catch (error) {
              console.warn(`Clipped travel mode ${defaultMode} failed; staying on drive:`, error);
              set({ selectedTravelMode: 'drive', availableTravelModes: availableModes });
            }
          } else {
            set({ selectedTravelMode: 'drive', availableTravelModes: availableModes });
          }

          // Process school IDs for enrollment data
          const schoolIds = new Set([
            refreshedSchool.uuid,
            ...Object.values(displayedNearby.current || {})
              .flatMap(schools => schools.map(s => s.uuid)),
            ...Object.values(displayedNearby.comparison || {})
              .flatMap(schools => schools.map(s => s.uuid)),
            ...Object.keys(displayedNearby.gradeData || {})
          ]);

          // Fetch enrollment data
          const enrollmentData = await schoolsApi.getSchoolsEnrollmentData(
            Array.from(schoolIds),
            refreshedSchool.uuid
          );
      
          const processedData = processEnrollmentData(enrollmentData);
          const categorizedData = categorizeSchools(
            processedData, 
            refreshedSchool.uuid
          );

          if (isStale()) return;

          // Update enrollment data state
          set(state => ({ 
            schoolsEnrollmentData: categorizedData,
            loadingStates: { ...state.loadingStates, enrollment: false }
          }));

          // Cache all the fetched data under the mode actually shown
          set(state => ({ 
            schoolDataCache: {
              ...state.schoolDataCache,
              [`${refreshedSchool.uuid}-${displayedMode}`]: {
                marketShareData: displayedEsri,
                nearbySchools: displayedNearby,
                rawNearbySchools: displayedNearby,
                projectionsData,
                enrollmentData: categorizedData,
                districtBoundaries: districtBoundariesData,
                cachedAt: new Date().toISOString()
              }
            },
            lastSchoolModified: new Date().toISOString(),
            contentLoading: false,
            error: null
          }));

        } catch (error) {
          throw error; // Let the outer catch handle it
        }

      } catch (error) {
        console.error('Error fetching school data:', error.message);
        if (isStale()) return;
        set({ 
          error: error.message, 
          contentLoading: false,
          loadingStates: {
            marketShare: false,
            nearby: false,
            projections: false,
            enrollment: false,
            districtBoundaries: false
          }
        });
      }
    },

    initializeSchool: async (school) => {
      try {
        set({ contentLoading: true });
        await get().selectSchool(school);
        set({ contentLoading: false });
      } catch (error) {
        console.error('Error initializing school:', error);
        set({ 
          contentLoading: false,
          error: error.message 
        });
      }
    },

    clearSchoolData: () => {
      const { selectedSchool } = get();
      if (selectedSchool) {
        const { schoolDataCache } = get();
        // The cache holds one entry per travel mode for a school, so drop them all.
        const remaining = Object.fromEntries(
          Object.entries(schoolDataCache)
            .filter(([key]) => !key.startsWith(`${selectedSchool.uuid}-`))
        );
        set({ schoolDataCache: remaining });
      }

      set({
        selectedSchool: null,
        esriData: null,
        nearbySchools: {},
        rawNearbySchools: {},
        enrollmentProjections: null,
        schoolsEnrollmentData: [],
        districtBoundaries: null,
        availableTravelModes: ['drive'],
        selectedTravelMode: 'drive',
        error: null
      });
    },

    changeTravelMode: async (travelMode) => {
      const { selectedSchool } = get();
      if (!selectedSchool) return;

      // Shares the sequence with selectSchool, so switching school mid-mode-change (or
      // vice versa) discards whichever request the user moved away from.
      const requestId = ++schoolRequestSequence;
      const isStale = () => schoolRequestSequence !== requestId;

      // Flag the dependent sections as loading up front. Without this the store
      // still holds the previous mode's data while the new mode is in flight, so
      // sections render populated and then collapse once the new data lands.
      set(state => ({
        selectedTravelMode: travelMode,
        loadingStates: {
          ...state.loadingStates,
          marketShare: true,
          nearby: true,
          enrollment: true
        }
      }));

      try {
        // ESRI and nearby schools are independent of each other for a given mode.
        const [newEsriData, newNearbySchools] = await Promise.all([
          schoolsApi.getMarketShareData(selectedSchool.uuid, null, travelMode)
            .then(data => {
              if (isStale()) return data;
              set(state => ({
                esriData: data,
                loadingStates: { ...state.loadingStates, marketShare: false }
              }));
              return data;
            }),

          schoolsApi.getAllNearbySchools(selectedSchool.uuid, null, travelMode)
            .then(data => {
              if (isStale()) return data;
              set(state => ({
                nearbySchools: data,
                rawNearbySchools: data,
                loadingStates: { ...state.loadingStates, nearby: false }
              }));
              return data;
            })
        ]);

        if (isStale()) return;

        // A mode change can surface neighbours the previous mode never returned. Without
        // fetching their enrollment the comparison section waits on data that never arrives.
        const schoolIds = new Set([
          selectedSchool.uuid,
          ...Object.values(newNearbySchools.current || {})
            .flatMap(schools => schools.map(s => s.uuid)),
          ...Object.values(newNearbySchools.comparison || {})
            .flatMap(schools => schools.map(s => s.uuid)),
          ...Object.keys(newNearbySchools.gradeData || {})
        ]);

        const enrollmentData = await schoolsApi.getSchoolsEnrollmentData(
          Array.from(schoolIds),
          selectedSchool.uuid
        );

        const categorizedData = categorizeSchools(
          processEnrollmentData(enrollmentData),
          selectedSchool.uuid
        );

        if (isStale()) return;

        set(state => ({
          schoolsEnrollmentData: categorizedData,
          loadingStates: { ...state.loadingStates, enrollment: false }
        }));

        // Store the result under this mode's key so a later selectSchool for the same
        // school serves the matching mode rather than whichever one happened to load last.
        set(state => ({
          schoolDataCache: {
            ...state.schoolDataCache,
            [`${selectedSchool.uuid}-${travelMode}`]: {
              marketShareData: newEsriData,
              nearbySchools: newNearbySchools,
              rawNearbySchools: newNearbySchools,
              projectionsData: state.enrollmentProjections,
              enrollmentData: categorizedData,
              districtBoundaries: state.districtBoundaries,
              cachedAt: new Date().toISOString()
            }
          }
        }));
      } catch (error) {
        console.error('Error changing travel mode:', error);
        if (isStale()) return;
        // Clear the loading flags so the sections fall back to rendering rather
        // than sitting on a skeleton forever.
        set(state => ({
          loadingStates: {
            ...state.loadingStates,
            marketShare: false,
            nearby: false,
            enrollment: false
          }
        }));
      }
    },

    markDataStale: () => set(state => ({
        // Don't clear map data completely, just mark as needing refresh
        contentLoading: true,
        loadingStates: {
          marketShare: true,
          nearby: true,
          projections: true,
          enrollment: true,
          districtBoundaries: true
        }
      })),

    clearAllData: () => {
      localStorage.removeItem('access_token'); // Clear token
      set({
        selectedSchool: null,
        esriData: null,
        nearbySchools: {},
        rawNearbySchools: {},
        enrollmentProjections: null,
        schoolsEnrollmentData: [],
        districtBoundaries: null,
        schoolDataCache: {},
        lastSchoolModified: null,
        schoolInitialized: false,
        availableTravelModes: ['drive'],
        selectedTravelMode: 'drive',
        error: null,
        schools: []
      });
    },

    // Force refresh schools from API (bypass cache)
    forceRefreshSchools: async () => {
      // Clear any cached data
      set({ schools: [], schoolsLoading: false });
      // Call fetchSchools which will hit the API
      return get().fetchSchools();
    }
  }))
);

export default useSchoolStore;